import React, { useState, useEffect, useRef } from 'react'
import { Button, Form, Input, message, Checkbox, Divider } from 'antd'
import classNames from 'classnames'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { useRouter } from 'next/router'
import Cookies from 'js-cookie'
import { useTranslation, Trans } from '@/libs/next-lang'
import Captcha from '@/components/Captcha'
import BaseLayout from '@/components/LoginModal/components/BaseLayout'
import globalStore from '@/globalStore'
import useFbe from '@/hooks/useFbe'
import CountDown from '@/page-components/components/CountDown'
import { sendVerifyCode } from '@/services/common'
import { goSSO, ssoButtonVisible } from '@/utils'
import { COOKIES_MAP } from '@/constants'
import { loginMobileReg, verifyCodeReg, loginPwdReg, emailReg } from '@/utils/validate'
import eventMap from '@/utils/gio/eventMap'
import gioTrackEvent from '@/utils/gio'
import styles from './index.module.less'

const Register = () => {
  const router = useRouter()
  const { language, isFbe } = useFbe()
  const globalMobx = useLocalObservable(() => globalStore)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [confirmDisabled, setConfirmDisabled] = useState(false)
  const [form] = Form.useForm()
  const captchaInsRef = useRef(null)

  const { t } = useTranslation(['user'])

  useEffect(() => {
    setConfirmDisabled(!form.getFieldValue('protocol'))
  }, [])

  /**
   * 获取验证码
   * @param starTiming
   */
  const handleStart = async (starTiming: () => void) => {
    try {
      const values = await form.validateFields(['account'])
      const account = values.account
      // 滑块校验
      const { validate } = await captchaInsRef.current.verify()
      starTiming()
      const { data = {} } = await sendVerifyCode({ account, validate })
      if (data && data.code === 0) {
        message.success(t('user:t1'))
        process.env.NODE_ENV !== 'production' &&
          data.result &&
          form.setFieldsValue({ captcha: data.result })
      }
    } catch (err) {
      console.error('发送验证码: ', err)
    }
  }

  /**
   * 提交注册
   */
  const handleOk = () => {
    form.validateFields().then(async values => {
      if (confirmLoading) return
      setConfirmLoading(true)
      let origin: any =
        Cookies.get(COOKIES_MAP.GIVEAWAY_CHANNEL, { domain: '.creativebooster.com' }) || ''
      if (origin?.toLocaleUpperCase() === 'SSO') {
        origin = ''
      }
      const params: any = Object.assign(
        isFbe ? { origin: 'FBE' } : origin ? { origin } : {},
        values,
      )
      const { success, data } = await globalMobx.register(params)
      if (success) {
        if (origin) {
          // 是从活动页面过来的
          gioTrackEvent(eventMap.CB_PC_from_edm_register_click_success, {
            cb_user_register_channel: origin,
          })
        } else {
          // 添加埋点
          gioTrackEvent(eventMap.CB_PC_register_click_success)
        }
        if (!isFbe) {
          // 跳转引导页
          router.replace('/welcome')
        }
      } else {
        if (data?.message) {
          message.error(data?.message)
        }
        setConfirmLoading(false)
      }
    })
  }

  return (
    <BaseLayout>
      <h3 className={styles['lrf-title']}>{t('user:t9')}</h3>
      <div className={styles.register}>
        <span className={styles['new-user']}>{t('user:t31')}？</span>
        <span
          className={classNames('link-button', styles['register-button'])}
          onClick={() => {
            globalMobx.currentAction = 'login'
          }}
        >
          {t('user:t20')}
        </span>
      </div>
      <div className={styles['lrf-main']}>
        <Form
          layout="vertical"
          requiredMark={false}
          name="verify_form"
          className={styles.main}
          form={form}
        >
          <Form.Item
            label=""
            name="account"
            style={{ marginBottom: 30 }}
            rules={[
              {
                required: true,
                message: t('user:t13'),
                whitespace: true,
              },
              () => ({
                validator(_, value) {
                  const v = (value || '').trim()
                  if (v && !loginMobileReg.test(v) && !emailReg.test(v)) {
                    return Promise.reject(new Error(t('user:t14')))
                  }
                  return Promise.resolve()
                },
              }),
            ]}
          >
            <Input
              suffix=" "
              size="large"
              placeholder={t('user:t15')}
              className="cover-autofill-bg-color"
            />
          </Form.Item>
          <Form.Item
            className={styles.password}
            style={{ marginBottom: 30 }}
            label=""
            name="password"
            rules={[
              {
                required: true,
                message: t('user:t16'),
                whitespace: true,
              },
              () => ({
                validator(_, value) {
                  const v = (value || '').trim()
                  if (v && !loginPwdReg.test(v)) {
                    return Promise.reject(new Error(t('components:regexp.t1')))
                  }
                  return Promise.resolve()
                },
              }),
            ]}
          >
            <Input.Password
              type="password"
              size="large"
              placeholder={t('user:t18')}
              className="cover-autofill-bg-color"
            />
          </Form.Item>
          <div
            style={{ display: 'flex', justifyContent: 'space-between' }}
            className={styles['register-captcha-form-item']}
          >
            <Form.Item
              className={styles.code}
              style={{
                flex: 1,
                // width: 'calc(100% - 146px)',
                // display: 'inline-block',
                marginBottom: 30,
                marginRight: 16,
              }}
              name="captcha"
              rules={[
                {
                  required: true,
                  message: t('user:t2'),
                  whitespace: true,
                },
                () => ({
                  validator(_, value) {
                    const v = (value || '').trim()
                    if (v && !verifyCodeReg.test(v)) {
                      return Promise.reject(new Error(t('user:t3')))
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <Input
                suffix=" "
                size="large"
                placeholder={t('user:t4')}
                className="cover-autofill-bg-color"
              />
            </Form.Item>
            <Form.Item
              style={{
                width: '130px',
                textAlign: 'right',
                display: 'inline-block',
                marginBottom: 30,
              }}
            >
              <div className={styles['psd-label']}>
                <CountDown
                  seconds={60}
                  render={(onStart, disabled, current) => {
                    if (disabled) {
                      return (
                        <Button
                          className={classNames(styles['verify-code-disabled'], styles['get-code'])}
                          size="large"
                          disabled={disabled}
                        >{`${current}s${language?.includes('zh') ? t('user:t5') : ''}`}</Button>
                      )
                    }
                    return (
                      <Button
                        onClick={() => handleStart(onStart)}
                        size="large"
                        className={styles['get-code']}
                      >
                        {t('user:t6')}
                      </Button>
                    )
                  }}
                />
              </div>
            </Form.Item>
          </div>
          <Captcha ref={captchaInsRef} lang={language} />
          <Form.Item
            style={{ marginBottom: 28 }}
            name="protocol"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error(t('user:t32'))),
              },
            ]}
          >
            <Checkbox
              className={styles['read-protocol-wrap']}
              onChange={checkedValue => {
                setConfirmDisabled(!checkedValue?.target?.checked)
              }}
            >
              <div style={{ fontSize: 12 }}>
                <Trans i18nKey="t40" ns="user">
                  我已阅读并同意
                  <span
                    style={{ fontSize: 12 }}
                    className="link-button"
                    onClick={() => {
                      window.open('/agreement')
                    }}
                  >
                    用户协议
                  </span>
                  和
                  <span
                    style={{ fontSize: 12 }}
                    className="link-button"
                    onClick={() => {
                      window.open('/policy')
                    }}
                  >
                    隐私政策
                  </span>
                </Trans>
              </div>
            </Checkbox>
          </Form.Item>
          <div className={styles['submit-form-item']}>
            <Button
              type="primary"
              className={styles['submit-button']}
              size="large"
              loading={confirmLoading}
              onClick={handleOk}
              disabled={confirmDisabled}
            >
              {t('user:t36')}
            </Button>
          </div>
          {ssoButtonVisible() && !isFbe && (
            <div className={classNames(styles['sso-wrapper'], styles['sso-register-wrapper'])}>
              <Divider plain className={styles['divider-line']}>
                {t('user:t10')}
              </Divider>
              <Button
                className={styles['sso-button']}
                onClick={() => {
                  // goSSO(`${window.location.origin}${globalMobx.targetPathUrl}`)
                  goSSO(`${window.location.origin}/sso`)
                }}
              >
                {t('user:t11')}
              </Button>
            </div>
          )}
        </Form>
      </div>
    </BaseLayout>
  )
}
export default observer(Register)
